import React, { useState } from 'react';
import BotMessage from '../bot-message';
import MathExpressionNew from '../../common/mathjax';
import { useTranslation } from 'react-i18next';
import { useAudioStore } from '../../../utils/audioStore';
import { isTTSAudioEnabled } from '../../../configs/config';
import { playAudioIcon, pauseAudioIcon, replayAudioIcon, quizCorrectV, quizWrongX } from '../../../assets/images/index';
import './lesson-quiz.scss';

interface LessonQuizProps {
  quiz: {
    question: string;
    options: Record<string, string>;
    correct_option: string;
  };
  onComplete?: () => void;
  onAnswer?: (isCorrect: boolean, selectedOption: string) => void;
  disabled?: boolean;
  skipAnimation?: boolean;
  playAudio?: boolean;
  id?: string;
  initialSelectedOption?: string | null;
  initialWrongOptions?: string[];
  stepNumber?: number;
  onReplayAudio?: () => void;
  onTogglePauseAudio?: () => void;
  isAudioPaused?: boolean;
}

const LessonQuiz: React.FC<LessonQuizProps> = ({
  quiz,
  onComplete,
  onAnswer,
  disabled,
  skipAnimation = false,
  playAudio = true,
  id = 'quiz',
  initialSelectedOption = null,
  initialWrongOptions = [],
  stepNumber,
  onReplayAudio,
  onTogglePauseAudio,
  isAudioPaused = false
}) => {
  const { t } = useTranslation();
  const playbackState = useAudioStore(state => state.playbackState);
  const [selectedOption, setSelectedOption] = useState<string | null>(initialSelectedOption);
  const [wrongOptions, setWrongOptions] = useState<string[]>(initialWrongOptions);
  const [isCorrectlyAnswered, setIsCorrectlyAnswered] = useState(!!initialSelectedOption && initialSelectedOption === quiz.correct_option);

  const targetId = id.replace(/^(quiz-|content-)/, '');
  const isThisItemActive = playbackState.activeId === targetId || playbackState.activeId === id;
  const isItemPaused = isThisItemActive && playbackState.isPaused;

  const handleOptionClick = (key: string) => {
    if (isCorrectlyAnswered || disabled || wrongOptions.includes(key)) return;
    const isCorrect = key === quiz.correct_option;
    
    if (!isCorrect) {
      setWrongOptions(prev => [...prev, key]);
      onAnswer?.(false, key);
    } else {
      setSelectedOption(key);
      setIsCorrectlyAnswered(true);
      onAnswer?.(true, key);
    }
  };

  return (
    <div className={`v2-lesson-quiz ${isCorrectlyAnswered ? 'v2-lesson-quiz--answered' : ''}`}>
      <BotMessage
        backgroundColor="#f2ffcf"
        onComplete={onComplete}
        skipAnimation={skipAnimation}
        playAudio={playAudio}
        id={id}
      >
        <div className="v2-lesson-quiz__content">
          <div className="v2-lesson-quiz__header">
            <span className="v2-lesson-quiz__title">{t('checkYourself')}{stepNumber !== undefined ? ` שלב ${stepNumber}` : ''}</span>
            {isTTSAudioEnabled() && (onTogglePauseAudio || onReplayAudio) && (
              <div className="v2-lesson-quiz__audio-actions" onClick={(e) => e.stopPropagation()}>
                {onTogglePauseAudio && (
                  <button
                    className="v2-lesson-quiz__audio-btn"
                    onClick={(e) => { e.stopPropagation(); onTogglePauseAudio(); }}
                    aria-label={isItemPaused ? 'Resume Audio' : 'Pause Audio'}
                    title={isItemPaused ? 'Resume Audio' : 'Pause Audio'}
                  >
                    <img 
                      src={isItemPaused ? playAudioIcon : pauseAudioIcon} 
                      alt={isItemPaused ? 'Play' : 'Pause'} 
                    />
                  </button>
                )}
                {onReplayAudio && (
                  <button
                    className="v2-lesson-quiz__audio-btn"
                    onClick={(e) => { e.stopPropagation(); onReplayAudio(); }}
                    aria-label={t('replayAudio')}
                    title={t('replayAudio')}
                  >
                    <img 
                      src={replayAudioIcon} 
                      alt="Replay" 
                    />
                  </button>
                )}
              </div>
            )}
          </div>
          <div>
            <span><MathExpressionNew content={quiz.question} /></span>
          </div>

          <div className="v2-lesson-quiz__options">
            {Object.entries(quiz.options).map(([key, value]) => {
              const isSelected = selectedOption === key;
              const isWrong = wrongOptions.includes(key);
              const isCorrect = key === quiz.correct_option;

              let optionClass = 'v2-lesson-quiz__option';
              if (isSelected) {
                optionClass += ' v2-lesson-quiz__option--selected';
                if (isCorrectlyAnswered && isCorrect) {
                  optionClass += ' v2-lesson-quiz__option--correct';
                }
              }
              if (isWrong) {
                optionClass += ' v2-lesson-quiz__option--wrong';
              }

              return (
                <div
                  key={key}
                  className={optionClass}
                  onClick={() => handleOptionClick(key)}
                >
                  <div className="v2-lesson-quiz__option-text">
                    <MathExpressionNew content={value} />
                  </div>
                  <div className="v2-lesson-quiz__radio-circle">
                    {isSelected && !isCorrectlyAnswered && <div className="v2-lesson-quiz__radio-inner" />}
                    {isCorrectlyAnswered && isSelected && isCorrect && <img src={quizCorrectV} alt="v" className="v2-lesson-quiz__correct-icon" />}
                    {isWrong && <img src={quizWrongX} alt="x" className="v2-lesson-quiz__wrong-icon" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </BotMessage>
    </div>
  );
};

export default LessonQuiz;
